(() => {
  if (customElements.get('product-size-chart')) return;

  const normalize = (value) =>
    (value === null || value === undefined ? '' : String(value)).trim().toLowerCase();

  class ProductSizeChart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.openButton = this.querySelector('[data-size-chart-open]');
      this.dialog = this.querySelector('[data-size-chart-dialog]');
      this.closeButton = this.querySelector('[data-size-chart-close]');
      this.table = this.querySelector('[data-size-chart-table]');
      this.footer = this.querySelector('[data-size-chart-footer]');
      this.selectedOutput = this.querySelector('[data-size-chart-selected]');
      this.addButton = this.querySelector('[data-size-chart-add]');
      this.addText = this.querySelector('[data-size-chart-add-text]');
      this.previouslyFocused = null;
      this.syncTimers = [];

      if (!this.openButton || !this.dialog || !this.closeButton) return;

      this.openButton.addEventListener('click', () => {
        this.openDialog();
      });

      this.closeButton.addEventListener('click', () => {
        this.closeDialog();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.closeDialog();
        }
      });

      // Nota: no interceptamos el evento 'cancel'. Al hacer preventDefault() y
      // cerrar manualmente, Chrome no vuelve a emitir 'close' y afterClose()
      // nunca corria (quedaba el scroll lock y aria-expanded="true").
      // Dejamos que Escape cierre el dialogo de forma nativa: eso si emite 'close'.

      this.dialog.addEventListener('close', () => {
        this.afterClose();
      });

      if (this.table) {
        this.initializeTableInteraction();
      }

      this.initializeProductSync();
    }

    /* ------------------------------------------------------------------
       Conexion con el selector de variantes y el boton de compra del tema
       ------------------------------------------------------------------ */

    initializeProductSync() {
      this.productScope = this.closest('product-info') || document;
      this.variantSelects = this.productScope.querySelector('variant-selects');
      this.themeAddButton = this.productScope.querySelector('[name="add"]');

      if (this.table && this.variantSelects) {
        this.querySelectorAll('[data-size-chart-select]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.selectSize(button.dataset.sizeChartSelect);
          });
        });

        this.table.addEventListener('click', (event) => {
          if (event.target.closest('[data-size-chart-select]')) return;

          const cell = this.getCell(event.target);

          if (!cell || !cell.dataset.sizeChartSize) return;

          this.selectSize(cell.dataset.sizeChartSize);
        });

        this.variantSelects.addEventListener('change', () => {
          this.scheduleSync();
        });
      }

      if (this.footer && this.themeAddButton) {
        this.footer.hidden = false;

        if (this.addButton) {
          this.addButton.addEventListener('click', () => {
            this.addToCart();
          });
        }

        this.themeButtonObserver = new MutationObserver(() => {
          this.syncFromTheme();
        });

        this.themeButtonObserver.observe(this.themeAddButton, {
          attributes: true,
          attributeFilter: ['disabled', 'class', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      this.syncFromTheme();
    }

    disconnectedCallback() {
      if (this.themeButtonObserver) {
        this.themeButtonObserver.disconnect();
      }

      this.syncTimers.forEach((timer) => window.clearTimeout(timer));
      this.syncTimers = [];
    }

    getChartSizes() {
      return Array.from(this.querySelectorAll('[data-size-chart-select]')).map(
        (button) => button.dataset.sizeChartSelect
      );
    }

    getControlValue(control) {
      if (!control) return '';

      if (typeof control.value === 'string' && control.value !== '') {
        return control.value;
      }

      return control.dataset ? control.dataset.value || '' : '';
    }

    // Busca, entre los grupos de opciones del producto, el que corresponde a la
    // talla: el que mas coincide con las columnas de la tabla. Asi funciona
    // igual en productos de una sola opcion y en Color + Talla.
    getSizeControls() {
      if (!this.variantSelects) return [];

      const chartSizes = this.getChartSizes().map(normalize);

      if (chartSizes.length === 0) return [];

      const wrappers = Array.from(
        this.variantSelects.querySelectorAll('[data-option-position]')
      );

      const groups = wrappers.length > 0 ? wrappers : [this.variantSelects];

      let bestControls = [];
      let bestScore = 0;

      groups.forEach((group) => {
        const controls = Array.from(
          group.querySelectorAll('input[type="radio"], [data-popout-option]')
        );

        const score = controls.filter((control) =>
          chartSizes.includes(normalize(this.getControlValue(control)))
        ).length;

        if (score > bestScore) {
          bestScore = score;
          bestControls = controls;
        }
      });

      return bestControls;
    }

    selectSize(size) {
      const target = this.getSizeControls().find(
        (control) => normalize(this.getControlValue(control)) === normalize(size)
      );

      if (!target) return false;

      if (target.matches('input[type="radio"]')) {
        const label = target.id
          ? this.productScope.querySelector(
              'label[for="' + (window.CSS && CSS.escape ? CSS.escape(target.id) : target.id) + '"]'
            )
          : null;

        if (label) {
          label.click();
        } else {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        target.click();
      }

      this.scheduleSync();

      return true;
    }

    scheduleSync() {
      [60, 350, 900].forEach((delay) => {
        this.syncTimers.push(
          window.setTimeout(() => {
            this.syncFromTheme();
          }, delay)
        );
      });
    }

    getSelectedSize() {
      const selected = this.getSizeControls().find((control) => {
        if (control.matches('input[type="radio"]')) {
          return control.checked;
        }

        return control.getAttribute('aria-current') === 'true';
      });

      return selected ? this.getControlValue(selected) : '';
    }

    syncFromTheme() {
      const selected = this.getSelectedSize();

      this.markSelectedColumn(selected);

      if (this.selectedOutput) {
        this.selectedOutput.textContent = selected || '—';
      }

      this.querySelectorAll('[data-size-chart-select]').forEach((button) => {
        const isCurrent =
          selected !== '' &&
          normalize(button.dataset.sizeChartSelect) === normalize(selected);

        button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      });

      if (!this.addButton || !this.themeAddButton) return;

      const themeLabel = this.themeAddButton.querySelector('.btn__text');
      const label = (themeLabel || this.themeAddButton).textContent.trim();

      if (label && this.addText && this.addText.textContent.trim() !== label) {
        this.addText.textContent = label;
      }

      this.addButton.disabled =
        this.themeAddButton.disabled ||
        this.themeAddButton.getAttribute('aria-disabled') === 'true';
    }

    markSelectedColumn(size) {
      if (!this.table) return;

      this.table
        .querySelectorAll('.is-column-selected')
        .forEach((cell) => cell.classList.remove('is-column-selected'));

      if (!size) return;

      const target = normalize(size);

      this.table.querySelectorAll('[data-size-chart-size]').forEach((cell) => {
        if (normalize(cell.dataset.sizeChartSize) === target) {
          cell.classList.add('is-column-selected');
        }
      });
    }

    addToCart() {
      if (!this.themeAddButton || this.themeAddButton.disabled) return;

      this.closeDialog();

      window.setTimeout(() => {
        this.themeAddButton.click();
      }, 180);
    }

    /* ------------------------------------------------------------------
       Dialogo
       ------------------------------------------------------------------ */

    openDialog() {
      this.previouslyFocused = document.activeElement;

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) {
          this.dialog.showModal();
        }
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('lgsc-scroll-lock');
      this.syncFromTheme();

      window.requestAnimationFrame(() => {
        this.closeButton.focus();
      });
    }

    closeDialog() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.afterClose();
      }
    }

    afterClose() {
      this.openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('lgsc-scroll-lock');
      this.clearHighlight();

      if (
        this.previouslyFocused &&
        this.previouslyFocused.isConnected &&
        typeof this.previouslyFocused.focus === 'function'
      ) {
        this.previouslyFocused.focus({ preventScroll: true });
      }
    }

    /* ------------------------------------------------------------------
       Resaltado cruzado
       ------------------------------------------------------------------ */

    initializeTableInteraction() {
      this.table.addEventListener('pointerover', (event) => {
        if (event.pointerType === 'touch') return;

        const cell = this.getCell(event.target);

        if (cell) {
          this.highlightCell(cell);
        }
      });

      this.table.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') {
          this.clearHighlight();
        }
      });

      this.table.addEventListener(
        'touchstart',
        (event) => {
          const cell = this.getCell(event.target);

          if (cell) {
            this.highlightCell(cell);
          }
        },
        { passive: true }
      );

      this.table.addEventListener(
        'touchmove',
        (event) => {
          const touch = event.touches[0];

          if (!touch) return;

          const element = document.elementFromPoint(
            touch.clientX,
            touch.clientY
          );

          const cell = this.getCell(element);

          if (cell) {
            this.highlightCell(cell);
          }
        },
        { passive: true }
      );

      this.dialog.addEventListener('pointerdown', (event) => {
        const isInsideTable =
          event.target instanceof Element &&
          event.target.closest('[data-size-chart-table]');

        if (!isInsideTable) {
          this.clearHighlight();
        }
      });
    }

    getCell(target) {
      if (!(target instanceof Element)) return null;

      const cell = target.closest('[data-size-chart-cell]');

      if (!cell || !this.table.contains(cell)) return null;

      return cell;
    }

    highlightCell(cell) {
      this.clearHighlight();

      const row = cell.parentElement;
      const rowCells = Array.from(row.cells);
      const columnIndex = rowCells.indexOf(cell);

      rowCells.forEach((rowCell) => {
        rowCell.classList.add('is-row-highlighted');
      });

      Array.from(this.table.rows).forEach((tableRow) => {
        const columnCell = tableRow.cells[columnIndex];

        if (columnCell) {
          columnCell.classList.add('is-column-highlighted');
        }
      });

      cell.classList.add('is-cell-highlighted');
    }

    clearHighlight() {
      if (!this.table) return;

      this.table
        .querySelectorAll(
          '.is-row-highlighted, .is-column-highlighted, .is-cell-highlighted'
        )
        .forEach((cell) => {
          cell.classList.remove(
            'is-row-highlighted',
            'is-column-highlighted',
            'is-cell-highlighted'
          );
        });
    }
  }

  customElements.define('product-size-chart', ProductSizeChart);
})();
